import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { useContext, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useCreateBenefitReportMutation } from "store/queries/benefitReports";
import { IAccount } from "types/Account/Account";

/**
 * One benefit a SuperAdmin can report. `formKey` is the editable form field;
 * `field` is the backend `BenefitField` enum value; `isCurrency` controls $ vs %.
 */
const BENEFIT_FIELDS = [
  {
    field: "Deductible",
    label: "Remaining Deductible",
    formKey: "deductible",
    isCurrency: true,
  },
  {
    field: "OutOfPocketMax",
    label: "Remaining Out-of-pocket Max",
    formKey: "outOfPocketMax",
    isCurrency: true,
  },
  {
    field: "FlatCopay",
    label: "Flat Copay",
    formKey: "flatCopay",
    isCurrency: true,
  },
  {
    field: "CoinsurancePercent",
    label: "Coinsurance Percent",
    formKey: "coinsurancePercent",
    isCurrency: false,
  },
] as const;

type BenefitFieldConfig = (typeof BENEFIT_FIELDS)[number];

export function ReportBenefitsModal({
  account,
  visible,
  onHide,
  initialField,
  source = "MANUAL_BUTTON",
  autoValue,
  correctedValue: correctedValueProp,
}: {
  account: IAccount;
  visible: boolean;
  onHide: () => void;
  initialField?: string;
  source?: "MANUAL_BUTTON" | "AUTO_DETECT";
  /** Pre-edit value (auto-detect). When omitted, falls back to the field's current value. */
  autoValue?: number | null;
  /** Post-edit value (auto-detect). When omitted, falls back to the field's current value. */
  correctedValue?: number | null;
}) {
  const { getValues } = useFormContext();
  const { current: toastElement } = useContext(ToastContext);
  const [createReport, { isLoading }] = useCreateBenefitReportMutation();

  const [selected, setSelected] = useState<BenefitFieldConfig>(
    BENEFIT_FIELDS[0]
  );
  const [autoSelectedValue, setAutoSelectedValue] = useState<number | null>(
    null
  );
  const [correctedValue, setCorrectedValue] = useState<number | null>(null);
  const [reason, setReason] = useState("");

  const fieldNumber = (formKey: string): number | null => {
    const v = getValues(formKey);
    return v != null && v !== "" ? Number(v) : null;
  };

  // When opened, focus the triggering field and populate auto/corrected. Auto-detect
  // passes explicit before/after values; the manual button falls back to the field's
  // current value (robust in redo, where benefitDetails is often null).
  useEffect(() => {
    if (!visible) return;
    const field =
      BENEFIT_FIELDS.find((f) => f.field === initialField) ?? BENEFIT_FIELDS[0];
    setSelected(field);
    setAutoSelectedValue(
      autoValue !== undefined ? autoValue : fieldNumber(field.formKey)
    );
    setCorrectedValue(
      correctedValueProp !== undefined
        ? correctedValueProp
        : fieldNumber(field.formKey)
    );
    setReason("");
  }, [visible, initialField]);

  const onSelectField = (fieldKey: string) => {
    const field =
      BENEFIT_FIELDS.find((f) => f.field === fieldKey) ?? BENEFIT_FIELDS[0];
    setSelected(field);
    setAutoSelectedValue(fieldNumber(field.formKey));
    setCorrectedValue(fieldNumber(field.formKey));
  };

  const formatValue = (val: number | null) => {
    if (val == null) return "—";
    return selected.isCurrency ? `$${val.toFixed(2)}` : `${val}%`;
  };

  const handleSubmit = async () => {
    const payer = getValues("payer");
    const practiceTypeCode = getValues("practiceType");
    const pverifyResponseId = getValues("pverifyResponseId");
    // The column has always existed but was never sent, so every stored report has a null
    // orderId. Only populated on draft-continue and redo, where the form is reset from a saved
    // order; on a brand-new order there is nothing to reference yet.
    const orderId = getValues("orderId");

    const body: Record<string, unknown> = {
      accountId: account.id,
      field: selected.field,
      reasonText: reason.trim(),
      source,
    };
    if (payer?.payerId != null) body.payerId = payer.payerId;
    if (practiceTypeCode) body.practiceTypeCode = practiceTypeCode;
    if (pverifyResponseId) body.pverifyResponseId = pverifyResponseId;
    if (orderId) body.orderId = orderId;
    if (autoSelectedValue != null) body.autoSelectedValue = autoSelectedValue;
    if (correctedValue != null) body.correctedValue = correctedValue;

    try {
      await createReport({ body }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Thanks for the report",
        detail: "We'll use this to improve future benefit auto-selection.",
      });
      onHide();
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Could not submit report",
        detail: "Please try again.",
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isLoading}
      />
      <Button
        label="Submit report"
        onClick={handleSubmit}
        loading={isLoading}
        disabled={!reason.trim()}
      />
    </div>
  );

  return (
    <Dialog
      header="Report benefits issue"
      visible={visible}
      style={{ width: "32rem" }}
      onHide={onHide}
      footer={footer}
      dismissableMask
    >
      <div className="flex flex-column gap-3">
        <p className="m-0 text-sm text-color-secondary">
          {source === "AUTO_DETECT"
            ? "You changed an auto-selected benefit. Tell us why so we can improve auto-selection."
            : "Tell us which auto-selected benefit looks wrong and why."}
        </p>

        <div className="flex flex-column gap-1">
          <label htmlFor="benefit-field" className="text-sm font-medium">
            Which benefit is wrong?
          </label>
          <Dropdown
            inputId="benefit-field"
            value={selected.field}
            options={BENEFIT_FIELDS.map((f) => ({
              label: f.label,
              value: f.field,
            }))}
            onChange={(e) => onSelectField(e.value)}
          />
        </div>

        <div className="flex gap-3">
          <div className="flex flex-column gap-1 flex-1">
            <span className="text-sm font-medium">Auto-selected</span>
            <span className="text-color-secondary">
              {formatValue(autoSelectedValue)}
            </span>
          </div>
          <div className="flex flex-column gap-1 flex-1">
            <label htmlFor="corrected-value" className="text-sm font-medium">
              Correct value
            </label>
            <InputNumber
              inputId="corrected-value"
              value={correctedValue}
              onValueChange={(e) => setCorrectedValue(e.value ?? null)}
              mode={selected.isCurrency ? "currency" : "decimal"}
              currency="USD"
              locale="en-US"
              suffix={selected.isCurrency ? undefined : "%"}
              minFractionDigits={selected.isCurrency ? 2 : 0}
              maxFractionDigits={2}
              min={0}
            />
          </div>
        </div>

        <div className="flex flex-column gap-1">
          <label htmlFor="benefit-reason" className="text-sm font-medium">
            Why is it wrong? <span className="text-red-500">*</span>
          </label>
          <InputTextarea
            id="benefit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            autoResize
            maxLength={2000}
            placeholder="e.g. Payer applied the ASC copay, not the office copay shown here."
          />
        </div>
      </div>
    </Dialog>
  );
}
